import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { isSupabaseConfigured, supabase } from '../lib/supabase'

export type AppRole = 'owner' | 'manager' | 'florist' | 'assistant' | 'consultant' | 'accountant'
export type Profile = { id:string; email:string; full_name:string|null; role:AppRole; is_system_admin:boolean; preferred_language:'pl'|'ru' }

type AuthContextValue = {
  user: User | null; profile: Profile | null; session: Session | null; loading: boolean; configured: boolean;
  signIn:(email:string,password:string)=>Promise<string|null>; resetPassword:(email:string)=>Promise<string|null>; signOut:()=>Promise<void>
}
const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(isSupabaseConfigured)

  useEffect(() => {
    if (!supabase) return
    const client = supabase
    const loadProfile = async (userId:string) => {
      const { data } = await client.from('profiles').select('*').eq('id', userId).maybeSingle()
      setProfile((data as Profile | null) ?? null)
    }
    client.auth.getSession().then(async ({ data }) => {
      setSession(data.session)
      if (data.session?.user) await loadProfile(data.session.user.id)
      setLoading(false)
    })
    const { data: listener } = client.auth.onAuthStateChange(async (_event, nextSession) => {
      setSession(nextSession)
      if (nextSession?.user) await loadProfile(nextSession.user.id); else setProfile(null)
      setLoading(false)
    })
    return () => listener.subscription.unsubscribe()
  }, [])

  const value = useMemo<AuthContextValue>(() => ({
    user: session?.user ?? null, profile, session, loading, configured: isSupabaseConfigured,
    signIn: async (email,password) => {
      if (!supabase) return 'Supabase is not configured.'
      const { error } = await supabase.auth.signInWithPassword({ email, password })
      return error?.message ?? null
    },
    resetPassword: async (email) => {
      if (!supabase) return 'Supabase is not configured.'
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin })
      return error?.message ?? null
    },
    signOut: async () => { if (supabase) await supabase.auth.signOut() }
  }), [session, profile, loading])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider')
  return context
}
